<template>
  <div class="cbg-auth__field-wrapper">
    <div :class="['cbg-auth__field', additionalClasses]">
      <input class="cbg-auth__field-input"
             ref="inputField"
             :id="inputName"
             :name="inputName"
             placeholder=" "
             autocomplete="off"
             :type="dataType"
             :value="modelValue || ''"
             :readonly="isReadOnly"
             :inputmode="inputMode"
             @focus="handleFocus"
             @focusout="handleBlur"
             @input="validateInput"/>
      <label class="cbg-auth__field-label" :for="inputName" @click="handleFocus">{{inputName}}{{ isRequired ? ' *' : ''}}</label>
      <div class="cbg-auth__field-icons">
        <button class="cbg-auth__field-button" type="button" v-show="isEditButton"  @click="handleEdit">
          <Svg class-name="cbg-auth__field-button__ico" icon-id="#edit"/>
        </button>
        <Svg v-if="isSuccessful" class-name="cbg-auth__field-icons__success" icon-id="#success"/>
      </div>
    </div>
    <p v-if="!isValid || isWrongRequest.taken || isWrongRequest.wrong || isWrongRequest.notVerified" class="cbg-auth__error">
      <Svg class-name="cbg-auth__error-ico" icon-id="#warning"/>
      <span v-if="!isValid || isWrongRequest.wrong" class="cbg-auth__error-text">Invalid {{errorInputName}}</span>
      <span v-else-if="isWrongRequest.taken" class="cbg-auth__error-text">This {{errorInputName}} is already taken</span>
      <span v-else class="cbg-auth__error-text">This {{errorInputName}} is not verified</span>
    </p>
    <p v-if="inputName === 'Username'" class="cbg-auth__warning">
      <Svg class-name="cbg-auth__warning-ico" icon-id="#warning"/>
      <span class="cbg-auth__warning-text">You won't be able to change your username after registration.</span>
    </p>
  </div>
</template>

<script>
import Svg from "@/components/global/Svg.vue";
import {ContextUpdateSymbol} from "@/components/global/Context.vue";
export default {
  name: 'Input',
  components: {
    Svg
  },
  props: {
    className: String,
    inputName: String,
    isRequired: {
      type: Boolean,
      default: false,
    },
    isLocked: {
      type: Boolean,
      default: false,
    },
    isEditable: {
      type: Boolean,
      default: false,
    },
    modelValue: String,
    dataType: {
      type: String,
      default: 'text',
    },
    validationType: {
      type: String,
      default: 'text',
    },
    isWrong: Object,
  },
  emits: ['update:modelValue'],
  data() {
    return {
      isFocused: false,
      isBlocked: !this.isFocused && this.modelValue,
      isEditVisible: true,
      isValid: true,
      isWrongRequest: {
        wrong: false,
        taken: false,
        notVerified: false,
      },
      validators: {
        email: (userInput) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userInput),
        text: (userInput) => /^(?![\d\s.,;'"/<{}>`~№_+=|?%&*()#!$^\\-]*$).{2,20}$/.test(userInput),
        phone: (userInput) => /^\+?\d{7,15}$/.test(userInput),
        login: (userInput) => /^(?!\d+$)[a-zA-Z0-9._-]{2,50}$/.test(userInput),
      },
    };
  },
  inject: {
    updateContext: { from: ContextUpdateSymbol, default: null },
  },
  computed: {
    inputMode() {
      return this.dataType === 'tel' ? 'tel' : 'text';
    },
    isPasswordType() {
      return this.dataType === 'password';
    },
    additionalClasses() {
      return [
        this.className,
        {
          'cbg-auth__field_locked': this.isBlocked && !this.isFocused,
          'cbg-auth__field_focused': this.isFocused,
          'cbg-auth__field_invalid': !this.isValid || this.isWrongRequest.wrong || this.isWrongRequest.taken || this.isWrongRequest.notVerified,
        }
      ];
    },
    isReadOnly() {
      return this.isLocked && (!this.isEditable || !this.isFocused)
    },
    isEditButton() {
      return this.isEditable && !this.isFocused && this.modelValue;
    },
    isSuccessful() {
      return this.isValid && this.modelValue && !this.isLocked && !this.isWrongRequest.wrong && !this.isWrongRequest.taken && !this.isWrongRequest.notVerified;
    },
    errorInputName() {
      return this.inputName !== 'CBG name' ? this.inputName.toLowerCase() : this.inputName;
    }
  },
  watch: {
    modelValue: function() {
      this.isBlocked = !this.isFocused && this.modelValue;
    },

    isWrong: {
      handler(newValue) {
        this.isWrongRequest = newValue;
      },
      deep: true,
    },
  },
  methods: {
    handleEdit() {
      this.isBlocked = false;

      if (this.isPasswordType) {
          this.updateContext && this.updateContext('step', 2);
      } else {
        this.$refs.inputField.focus();
        this.isEditVisible = false;
        this.isFocused = true;
      }
    },

    handleFocus(event) {
      if (this.isBlocked && this.isReadOnly) {
        event.preventDefault();
        return;
      }

      if (this.validationType === 'phone' && !this.modelValue) {
        this.$emit('update:modelValue', `+`);
      }
      this.isWrongRequest.wrong = false;
      this.isWrongRequest.taken = false;
      this.isWrongRequest.notVerified = false;
      this.isFocused = true;
    },

    handleBlur() {
      this.isFocused = false;

      if (this.modelValue) {
        this.isBlocked = true;
        this.isEditVisible = true;
      }

      if (this.validationType === 'phone' && this.modelValue?.length === 1) {
        this.$emit('update:modelValue', '');
        this.isBlocked = false;
        this.isEditVisible = false;
      }
    },

    validateInput(event) {
      const userInput = event ? event.target.value.trim() : this.$refs.inputField.value;

      const validationFunction = this.validators[this.validationType];
      this.isValid = validationFunction ? validationFunction(userInput) : true;

      if (!this.isRequired && !userInput) {
        this.isValid = true;
      }

      this.$emit('update:modelValue', userInput);
    },
  },
}
</script>